/**
 * The selected SEP environment (doc 18).
 *
 * Module-level state rather than React state, for the same reason the token is:
 * `api/client.ts` has to read it on every request and is not a component. The
 * provider subscribes and re-renders; the client just reads.
 *
 * Persisted in localStorage *and* mirrored into the URL. The URL matters more
 * than it looks: a screenshot, a pasted link or a reloaded tab all carry which
 * installation was being looked at, so "the licences are wrong!" comes with the
 * answer attached.
 */
import { SEP_AMBER, SEP_BLUE, SEP_RED } from '@/theme';
import type { SepEnvironment } from '@/types/api';

const STORAGE_KEY = 'sep.environment';
const QUERY_PARAM = 'env';

export const ENVIRONMENTS: readonly SepEnvironment[] = [
  'production',
  'staging',
  'development',
] as const;

export const DEFAULT_ENVIRONMENT: SepEnvironment = 'production';

interface EnvironmentStyle {
  label: string;
  /** Short form for the mobile app bar, where the full word does not fit. */
  short: string;
  colour: string;
  description: string;
}

/**
 * Every environment is loud, including production.
 *
 * The usual convention paints non-production in warning colours and leaves
 * production looking normal. That protects against the wrong mistake. The
 * dangerous error here is acting on production while believing you are in
 * development — and a quiet, unremarkable production bar is exactly what lets
 * someone stop noticing it. There is no neutral state to be lulled by: all
 * three announce themselves, and production announces itself the loudest.
 */
export const ENVIRONMENT_STYLE: Record<SepEnvironment, EnvironmentStyle> = {
  production: {
    label: 'Production',
    short: 'PROD',
    colour: SEP_RED,
    description: 'Echte Kundendaten. Änderungen wirken sofort.',
  },
  staging: {
    label: 'Staging',
    short: 'STAGING',
    colour: SEP_AMBER,
    description: 'Testumgebung. Eigene Mandanten, Benutzer und Tokens.',
  },
  development: {
    label: 'Development',
    short: 'DEV',
    colour: SEP_BLUE,
    description: 'Entwicklungsumgebung. Eigene Mandanten, Benutzer und Tokens.',
  },
};

function isEnvironment(value: string | null): value is SepEnvironment {
  return value !== null && (ENVIRONMENTS as readonly string[]).includes(value);
}

function readInitial(): SepEnvironment {
  // The URL wins over the stored value: a link someone was sent names an
  // environment on purpose, and it should not be overruled by whatever that
  // browser last looked at.
  try {
    const fromUrl = new URLSearchParams(window.location.search).get(QUERY_PARAM);
    if (isEnvironment(fromUrl)) return fromUrl;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isEnvironment(stored)) return stored;
  } catch {
    /* private mode, or no DOM under test */
  }
  return DEFAULT_ENVIRONMENT;
}

let active: SepEnvironment = readInitial();
const listeners = new Set<(environment: SepEnvironment) => void>();

export function currentEnvironment(): SepEnvironment {
  return active;
}

export function setEnvironment(next: SepEnvironment): void {
  if (next === active) return;
  active = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
    const url = new URL(window.location.href);
    url.searchParams.set(QUERY_PARAM, next);
    window.history.replaceState(null, '', url.toString());
  } catch {
    /* the selection still applies for this page */
  }
  listeners.forEach((listener) => listener(next));
}

export function subscribeToEnvironment(
  listener: (environment: SepEnvironment) => void,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Put the current environment in the URL without changing it. */
export function syncEnvironmentToUrl(): void {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get(QUERY_PARAM) === active) return;
    url.searchParams.set(QUERY_PARAM, active);
    window.history.replaceState(null, '', url.toString());
  } catch {
    /* ignore */
  }
}
